import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { LANGUAGES, STORAGE_KEY } from '../../i18n/languages';
import './LangSwitcher.css';

interface LangSwitcherProps {
  /** "light" = white text on dark bg; "dark" = dark text on light bg (default). */
  tone?: 'light' | 'dark';
  className?: string;
}

export const LangSwitcher: React.FC<LangSwitcherProps> = ({
  tone = 'dark',
  className = '',
}) => {
  const { i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  const current =
    LANGUAGES.find((l) => l.code === i18n.resolvedLanguage) ??
    LANGUAGES.find((l) => l.code === i18n.language) ??
    LANGUAGES[1]; // en fallback

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  const pick = (code: string) => {
    i18n.changeLanguage(code);
    try {
      localStorage.setItem(STORAGE_KEY, code);
    } catch {
      /* localStorage unavailable */
    }
    setOpen(false);
  };

  return (
    <div
      ref={rootRef}
      className={`mg-lang mg-lang--${tone} ${className}`}
    >
      <button
        type="button"
        className="mg-lang__trigger"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <svg
          width="14"
          height="14"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.75"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden
        >
          <circle cx="12" cy="12" r="9" />
          <path d="M3 12h18" />
          <path d="M12 3a13 13 0 0 1 0 18 13 13 0 0 1 0-18z" />
        </svg>
        <span className="mg-lang__current">{current.native}</span>
      </button>

      {open && (
        <ul className="mg-lang__menu" role="listbox">
          {LANGUAGES.map((l) => {
            const active = l.code === current.code;
            return (
              <li key={l.code} role="option" aria-selected={active}>
                <button
                  type="button"
                  className={`mg-lang__item ${
                    active ? 'mg-lang__item--active' : ''
                  }`}
                  onClick={() => pick(l.code)}
                >
                  {l.native}
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};
